import React from 'react';
import PropTypes from 'prop-types';
import {scaleLinear} from 'd3-scale';
import {arc as createArcFactory} from 'd3-shape';
import {t} from 'common/utils/translation-util';
import {ReportDynamicGaugeDirectionEnum} from 'common/proxies/enum-proxy';

export const ERROR_MESSAGES = {
  NOT_TWO_CUSTOM_BREAKPOINTS: t(
    'Exactly two custom breakpoints must be provided.',
  ),
  CUSTOM_BREAKPOINTS_OUT_OF_ORDER: t(
    'Custom breakpoints must be provided in order.',
  ),
  CUSTOM_BREAKPOINT_GREATER_THAN_OR_EQUAL_TO_MAX: t(
    'Custom breakpoints must be smaller than the maximum.',
  ),
  CUSTOM_BREAKPOINT_LESS_THAN_OR_EQUAL_TO_MIN: t(
    'Custom breakpoints must be greater than the minimum',
  ),
  MIN_GREATER_THAN_OR_EQUAL_TO_MAX: t(
    'Minimum must be smaller than the maximum',
  ),
};
export const STATUS_MESSAGES = {
  green: t('A gauge colored green, to indicate a good status.'),
  yellow: t(
    'A gauge colored yellow, to indicate a moderate or warning status.',
  ),
  red: t(
    'A gauge colored red, to indicate an unwanted, bad, or critical status.',
  ),
};
export const COLORS = {
  green: '#0cce6b',
  yellow: '#dced31',
  red: '#ef2d56',
  grey: '#dbdbdb',
  white: '#fffce8',
};

function getBreakpoints(minimum, maximum, givenBreakpoints) {
  return givenBreakpoints?.filter((bp) => bp !== null).length > 0
    ? getCustomBreakpoints(givenBreakpoints)
    : getDefaultBreakpoints(minimum, maximum);
}

function getDefaultBreakpoints(minimum, maximum) {
  return [
    minimum + (maximum - minimum) / 3,
    minimum + (2 * (maximum - minimum)) / 3,
  ];
}

/**
 * Basically, this function just checks if they're out of order,
 * or if there are more than or fewer than two custom breakpoints,
 * and if so, it throws.
 *
 * This is important for now because it implies the gauge was
 * configured incorrectly -- but later, if this is used in
 * other places, it may be okay to remove.
 *
 * At the time of this writing, this gauge is only expected for
 * use in the ReadoutCard component.
 */
function getCustomBreakpoints(breakpoints) {
  let isSorted = true;

  if (breakpoints.length !== 2) {
    throw new Error(ERROR_MESSAGES.NOT_TWO_CUSTOM_BREAKPOINTS);
  }

  breakpoints.forEach((bp, index) => {
    if (bp > breakpoints[index + 1]) {
      isSorted = false;
    }
  });

  if (!isSorted) {
    throw new Error(ERROR_MESSAGES.CUSTOM_BREAKPOINTS_OUT_OF_ORDER);
  }

  return breakpoints;
}

function checkBoundaries(minimum, maximum, breakpoints) {
  if (minimum >= maximum) {
    throw new Error(ERROR_MESSAGES.MIN_GREATER_THAN_OR_EQUAL_TO_MAX);
  }

  if (breakpoints.some((bp) => bp > maximum)) {
    throw new Error(
      ERROR_MESSAGES.CUSTOM_BREAKPOINT_GREATER_THAN_OR_EQUAL_TO_MAX,
    );
  }

  if (breakpoints.some((bp) => bp < minimum)) {
    throw new Error(ERROR_MESSAGES.CUSTOM_BREAKPOINT_LESS_THAN_OR_EQUAL_TO_MIN);
  }
}

function getStatusMessage(breakpoints, direction, value) {
  const [leftMax, midMax] = breakpoints;

  if (value < leftMax) {
    return direction === ReportDynamicGaugeDirectionEnum.GREEN_TO_RED.id
      ? STATUS_MESSAGES.green
      : STATUS_MESSAGES.red;
  }

  if (value < midMax) {
    return STATUS_MESSAGES.yellow;
  }

  return direction === ReportDynamicGaugeDirectionEnum.GREEN_TO_RED.id
    ? STATUS_MESSAGES.red
    : STATUS_MESSAGES.green;
}

function getGaugeArcs(minimum, maximum, breakpoints, direction, value) {
  const scaleForGauge = scaleLinear(
    [minimum, maximum],
    [-Math.PI / 2, Math.PI / 2],
  );

  let color = '';
  if (value < breakpoints[0]) {
    color =
      direction === ReportDynamicGaugeDirectionEnum.GREEN_TO_RED.id
        ? COLORS.green
        : COLORS.red;
  } else if (value < breakpoints[1]) {
    color = COLORS.yellow;
  } else {
    color =
      direction === ReportDynamicGaugeDirectionEnum.GREEN_TO_RED.id
        ? COLORS.red
        : COLORS.green;
  }

  const arcs = [
    {key: 'background', val: maximum, fill: COLORS.grey},
    {key: 'arc', val: value, fill: color},
  ].map(({key, val, fill}) => {
    const clampedValue = Math.min(maximum, Math.max(minimum, val));
    const startAngle = scaleForGauge(minimum);
    const endAngle = scaleForGauge(clampedValue);

    return {
      key,

      d: createArc({
        startAngle,
        endAngle,
      }),
      fill,
    };
  });

  return arcs;
}

const WIDTH = 150;
const HEIGHT = 75;
const INNER_RADIUS_PX = 40;
const OUTER_RADIUS_PX = 60;

const createArc = createArcFactory()
  .innerRadius(INNER_RADIUS_PX)
  .outerRadius(OUTER_RADIUS_PX)
  .cornerRadius(0);

function ReadoutCardDynamicGauge({
  minimum,
  maximum,
  breakpoints: givenBreakpoints = [],
  direction = ReportDynamicGaugeDirectionEnum.GREEN_TO_RED.id,
  value,
}) {
  const breakpoints = getBreakpoints(minimum, maximum, givenBreakpoints);
  checkBoundaries(minimum, maximum, breakpoints);

  const status = getStatusMessage(breakpoints, direction, value);
  const arcs = getGaugeArcs(minimum, maximum, breakpoints, direction, value);

  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
      <title>{status}</title>
      <g transform={`translate(${WIDTH / 2}, ${HEIGHT - 2})`}>
        {arcs.map((arc) => (
          <path d={arc.d} fill={arc.fill} key={arc.key} />
        ))}
      </g>
    </svg>
  );
}

ReadoutCardDynamicGauge.propTypes = {
  breakpoints: PropTypes.array,
  direction: PropTypes.string,
  maximum: PropTypes.number.isRequired,
  minimum: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default ReadoutCardDynamicGauge;
